import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

type Theme = {
	dataTestid: string;
	statusText: string;
	wrapperClass: string;
	textClass: string;
};

export const NotifiedOrRespondedStatus: React.FC<{
	daysSinceNotified: number | null | undefined;
	isNotified: boolean;
	hasResponded: boolean;
}> = ({ daysSinceNotified, isNotified, hasResponded }) => {
	const awaitingResponse = !isNotified && !hasResponded;

	const getNotifiedSinceText = () => {
		if (awaitingResponse) return 'Awaiting response';
		if (daysSinceNotified === null || daysSinceNotified === undefined) return 'Not responded';
		if (daysSinceNotified === 0) return 'No response. Notified today';
		if (daysSinceNotified === 1) return 'No response. Notified 1 day ago';
		return `No response. Notified ${daysSinceNotified} days ago`;
	};

	let theme: Theme;

	if (hasResponded) {
		theme = {
			dataTestid: 'responded-status',
			statusText: 'Responded',
			wrapperClass: 'bg-[#EEFFF5] border-[#0DDA62]',
			textClass: 'text-typography-success',
		};
	} else if (awaitingResponse) {
		theme = {
			dataTestid: 'awaiting-status',
			statusText: getNotifiedSinceText(),
			wrapperClass: 'bg-white border-[#918A9D]',
			textClass: 'text-typography-strong',
		};
	} else {
		theme = {
			dataTestid: 'notified-status',
			statusText: getNotifiedSinceText(),
			wrapperClass: 'bg-[#FEF1F3] border-[#FF9DAD]',
			textClass: 'text-typography-brand',
		};
	}

	const baseWrapper = 'inline-flex items-center rounded-full border px-2 py-0.5';

	return (
		<div data-testid={theme.dataTestid}>
			<div className={cn(baseWrapper, theme.wrapperClass)}>
				<Text size="small" variant="body" fontWeight="bold" className={theme.textClass}>
					{theme.statusText}
				</Text>
			</div>
		</div>
	);
};
